import React, { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const OAuthCallbackPage: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const { t } = useTranslation('common');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const handleOAuthCallback = async () => {
      const params = new URLSearchParams(location.search);
      const code = params.get('code');
      const errorParam = params.get('error');
      const errorDescription = params.get('error_description');

      // Handle OAuth error
      if (errorParam) {
        console.error('OAuth error:', errorParam, errorDescription);
        setError(errorDescription || 'Authentication failed');
        setTimeout(() => {
          history.push('/login');
        }, 3000);
        return;
      }

      // Handle missing code
      if (!code) {
        console.error('No authorization code received');
        setError('No authorization code received');
        setTimeout(() => {
          history.push('/login');
        }, 3000);
        return;
      }

      try {
        // Exchange authorization code for tokens
        const domain = process.env.REACT_APP_OAUTH_DOMAIN;
        const clientId = process.env.REACT_APP_USER_POOL_CLIENT_ID;
        const redirectUri = `${window.location.origin}/auth/callback`;

        const tokenResponse = await fetch(`https://${domain}/oauth2/token`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: new URLSearchParams({
            grant_type: 'authorization_code',
            client_id: clientId!,
            code: code,
            redirect_uri: redirectUri,
          }),
        });

        if (!tokenResponse.ok) {
          const errorBody = await tokenResponse.text();
          console.error('Token exchange failed:', tokenResponse.status, errorBody);
          throw new Error(`Token exchange failed (${tokenResponse.status}): ${errorBody}`);
        }

        const tokens = await tokenResponse.json();

        // Store tokens in local storage (Cognito SDK format)
        const idTokenKey = `CognitoIdentityServiceProvider.${clientId}.LastAuthUser`;
        const username = extractUsernameFromToken(tokens.id_token);

        if (username) {
          localStorage.setItem(idTokenKey, username);
          localStorage.setItem(
            `CognitoIdentityServiceProvider.${clientId}.${username}.idToken`,
            tokens.id_token
          );
          localStorage.setItem(
            `CognitoIdentityServiceProvider.${clientId}.${username}.accessToken`,
            tokens.access_token
          );
          localStorage.setItem(
            `CognitoIdentityServiceProvider.${clientId}.${username}.refreshToken`,
            tokens.refresh_token
          );

          localStorage.setItem(
            `CognitoIdentityServiceProvider.${clientId}.${username}.clockDrift`,
            '0'
          );
        }

        // Redirect to tracks page after successful login (full reload to re-initialize auth context)
        window.location.href = '/tracks';
      } catch (error) {
        console.error('OAuth callback error:', error);
        setError('Authentication failed. Please try again.');
        setTimeout(() => {
          history.push('/login');
        }, 3000);
      }
    };

    handleOAuthCallback();
  }, [history, location.search]);

  // Helper function to extract username from JWT token
  const extractUsernameFromToken = (idToken: string): string | null => {
    try {
      const payload = idToken.split('.')[1];
      const decodedPayload = JSON.parse(atob(payload));
      return decodedPayload['cognito:username'] || decodedPayload.email || null;
    } catch (error) {
      console.error('Failed to decode token:', error);
      return null;
    }
  };

  return (
    <div
      className='min-h-screen flex items-center justify-center'
      style={{ background: 'rgb(8 6 20 / 100%)' }}
    >
      <div className='text-center animate-fade-in'>
        {error ? (
          <>
            <div className='mb-6'>
              <svg
                className='w-16 h-16 mx-auto'
                fill='none'
                stroke='#ef4444'
                viewBox='0 0 24 24'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M6 18L18 6M6 6l12 12'
                />
              </svg>
            </div>
            <h2
              className='text-2xl font-bold mb-4'
              style={{ color: 'var(--text-primary)' }}
            >
              {t('authError')}
            </h2>
            <p className='text-base mb-4' style={{ color: 'var(--text-secondary)' }}>
              {error}
            </p>
            <p className='text-sm' style={{ color: 'var(--text-secondary)' }}>
              {t('redirectingToLogin')}
            </p>
          </>
        ) : (
          <>
            <div className='spinner glow mx-auto mb-6'></div>
            <h2
              className='text-2xl font-bold mb-4'
              style={{ color: 'var(--text-primary)' }}
            >
              {t('authenticating')}
            </h2>
            <p className='text-base' style={{ color: 'var(--text-secondary)' }}>
              {t('pleaseWaitLogin')}
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default OAuthCallbackPage;
